import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useId,
  useCallback,
  memo,
} from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import api from "@/services/api";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  PaginationState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleX,
  Columns3,
  Ellipsis,
  Plus,
  Trash,
  Calendar,
  Search,
  ListFilter,
  User,
  UserCheck,
  Loader2Icon,
  Eye,
  IndianRupee,
  CreditCard,
  CheckCircle,
  Download,
  Loader2,
  GraduationCap,
  MessageSquare,
  MoreHorizontal,
} from "lucide-react";
import StudentFilterDrawer, { StudentFilters } from "./StudentFilterDrawer";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "../ui/loader";
import AddStudentDialog from "../staff/AddStudentDialog";
import PaymentFormDialog from "../accountant/PaymentFormDialog";
import {
  studentService,
  StudentFeeStructureResponse,
} from "../../services/studentService";
import { IconWhatsapp } from "../icons/IconWhatspp";

interface Student {
  id: string;
  studentId: string;
  rollNumber: string;
  username: string;
  firstName: string;
  lastName: string;
  fatherName?: string;
  motherName?: string;
  guardianName?: string;
  email: string;
  dob?: string;
  gender?: string;
  studentPhone?: string;
  fatherPhone?: string;
  motherPhone?: string;
  guardianPhone?: string;
  address?: string;
  intermediateGroup?: string;
  intermediateMarks?: string;
  intermediateCollege?: string;
  medium?: string;
  academicYear?: string;
  batchYear?: string;
  totalFee?: string;
  discountAmount?: string;
  discountReason?: string;
  finalFee?: string;
  paidAmount?: number;
  dueAmount?: number;
  enrollmentStatus?: string;
  profileCompleted?: boolean;
  phone?: string; // Keep for backward compatibility
  courseGroup: {
    id: string;
    name: string;
    code: string;
    durationYears?: number;
    totalFee?: string;
    description?: string;
  };
  college: {
    id: string;
    address: string;
    code: string;
    name: string;
    phone?: string;
    email?: string;
    establishedYear?: number;
  };
  enrollmentDate: string;
  isActive?: boolean;
}

interface FeePayment {
  id: string;
  receiptNumber: string;
  amount: number;
  paymentMode: string;
  transactionId?: string;
  paymentDate: Date;
  semester?: string;
  description?: string;
}

interface StudentFinancialDetails {
  id: string;
  studentId: string;
  rollNumber: string;
  firstName: string;
  lastName: string;
  fatherName: string;
  motherName?: string;
  guardianName?: string;
  studentPhone?: string;
  fatherPhone?: string;
  motherPhone?: string;
  guardianPhone?: string;
  courseGroup: {
    name: string;
    code: string;
  };
  totalFee: number;
  paidAmount: number;
  dueAmount: number;
  currentYearFee?: number;
  currentYearPaid?: number;
  currentYearDue?: number;
  currentAcademicYear?: string;
  payments: FeePayment[];
}

// Custom filter function for multi-column searching
const multiColumnFilterFn: FilterFn<Student> = (row, columnId, filterValue) => {
  const searchableRowContent =
    `${row.original.firstName} ${row.original.lastName} ${row.original.rollNumber} ${row.original.email} ${row.original.courseGroup.name} ${row.original.college.name}`.toLowerCase();
  const searchTerm = (filterValue ?? "").toLowerCase();
  return searchableRowContent.includes(searchTerm);
};

// Memoized search input component to prevent focus loss
interface SearchInputProps {
  id: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  searchInProgress: boolean;
}

const SearchInput = memo<SearchInputProps>(
  ({ id, searchTerm, onSearchChange, onSearchClear, searchInProgress }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    return (
      <div className="relative bg-white flex-1">
        <Input
          id={`${id}-input`}
          ref={inputRef}
          className={cn("ps-9", Boolean(searchTerm) && "pe-9")}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Filter by name, roll number, email, or course..."
          type="text"
          aria-label="Filter by name, roll number, email, or course"
        />
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50">
          {searchInProgress ? (
            <Loader2Icon className="animate-spin h-4 w-4" />
          ) : (
            <Search className="stroke-2 h-4 w-4" aria-hidden="true" />
          )}
        </div>
        {Boolean(searchTerm) && (
          <button
            className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-lg text-muted-foreground/80 outline-offset-2 transition-colors hover:text-foreground focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Clear filter"
            onClick={onSearchClear}
          >
            <CircleX className="stroke-2 h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }
);

// Row actions component
interface RowActionsProps {
  row: Row<Student>;
  onEdit: (student: Student) => void;
  onRefresh: () => void;
  userRole?: string;
  onViewFinancialDetails?: (studentId: string) => void;
  onAddPayment?: (student: Student) => void;
}

const RowActions: React.FC<RowActionsProps> = ({
  row,
  onEdit,
  onRefresh,
  userRole,
  onViewFinancialDetails,
  onAddPayment,
}) => {
  const student = row.original;

  const handleDelete = async () => {
    try {
      await api.delete(`/students/permanent/${student.id}`);
      onRefresh();
    } catch (error) {
      console.error("Failed to delete student:", error);
    }
  };

  return (
    <div className="flex justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-label="Edit item"
          >
            <Ellipsis className="stroke-2 h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[180px]">
          <DropdownMenuGroup>
            {!["ACCOUNTANT", "SYSTEM_ADMIN"].includes(userRole!) && (
              <DropdownMenuItem onClick={() => onEdit(student)}>
                Edit
              </DropdownMenuItem>
            )}
            {["ACCOUNTANT"].includes(userRole!) && onAddPayment && (
              <DropdownMenuItem onClick={() => onAddPayment(student)}>
                Add Payment
              </DropdownMenuItem>
            )}

            {/* Financial actions for ACCOUNTANT role */}
            {["ACCOUNTANT", "COLLEGE_ADMIN"].includes(userRole!) &&
              onViewFinancialDetails && (
                <DropdownMenuItem
                  onClick={() => onViewFinancialDetails(student.id)}
                >
                  View Payment Details
                </DropdownMenuItem>
              )}

            {!["ACCOUNTANT", "SYSTEM_ADMIN"].includes(userRole!) && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="text-destructive focus:text-destructive"
                  >
                    Permanently delete
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      the student record for {student.firstName}{" "}
                      {student.lastName}.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const Students = () => {
  const componentId = useId();

  // Get user role for conditional features
  const { user } = useSelector((state: RootState) => state.auth);

  // Local state management (no longer using Redux for filtered results)
  const [students, setStudents] = useState<Student[]>([]);
  const [localPagination, setLocalPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
    limit: 10,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Local state for table functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    // Show some key columns by default to demonstrate horizontal scrolling
    isActive: false,
    fatherName: false,
    motherName: false,
    collegeName: ["SYSTEM_ADMIN"].includes(user?.role!),
    email: ["STAFF"].includes(user?.role!),
    enrollmentDate: ["STAFF"].includes(user?.role!),
    paidAmount: ["ACCOUNTANT", "COLLEGE_ADMIN"].includes(user?.role!),
    guardianName: false,
    gender: false,
    address: false,
    intermediateGroup: false,
    medium: false,
    academicYear: ["STAFF"].includes(user?.role!),
    courseGroupName: ["ACCOUNTANT", "COLLEGE_ADMIN"].includes(user?.role!),
    totalFee: ["ACCOUNTANT", "COLLEGE_ADMIN"].includes(user?.role!), // Show detailed fee column for all except financial roles
    totalFeeAmount: false, // Show financial columns for COLLEGE_ADMIN and ACCOUNTANT
    paidAmountFinancial: false, // Show financial columns for COLLEGE_ADMIN and ACCOUNTANT
    dueAmountFinancial: false, // Show financial columns for COLLEGE_ADMIN and ACCOUNTANT
    profileCompleted: false,
  });
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Dialog states
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [addStudentDialogOpen, setAddStudentDialogOpen] = useState(false);

  // Filter drawer states
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<StudentFilters>({
    collegeId: [],
    courseGroupId: [],
    batchYear: [],
    enrollmentStatus: [],
    enrollmentStartDate: "",
    enrollmentEndDate: "",
  });

  // Payment-related state for ACCOUNTANT role
  const [selectedStudent, setSelectedStudent] =
    useState<StudentFinancialDetails | null>(null);
  const [studentFeeStructure, setStudentFeeStructure] =
    useState<StudentFeeStructureResponse | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showStudentSearchDialog, setShowStudentSearchDialog] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState<any>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [searchRollNumber, setSearchRollNumber] = useState("");
  const [searchedStudent, setSearchedStudent] = useState<any>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showPaymentSuccessDialog, setShowPaymentSuccessDialog] =
    useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState<any>(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [downloadingReceiptNumber, setDownloadingReceiptNumber] = useState<
    string | null
  >(null);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== debouncedSearchTerm) {
        setDebouncedSearchTerm(searchTerm);
        setSearchInProgress(false);
      }
    }, 500); // 500ms delay

    if (searchTerm !== debouncedSearchTerm) {
      setSearchInProgress(true);
    }

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  // Fetch data using filter endpoint
  useEffect(() => {
    const filterParams: any = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm || undefined,
    };

    // Add filter parameters if they exist
    if (activeFilters.collegeId.length > 0) {
      filterParams.collegeId = activeFilters.collegeId.join(",");
    }
    if (activeFilters.courseGroupId.length > 0) {
      filterParams.courseGroupId = activeFilters.courseGroupId.join(",");
    }
    if (activeFilters.batchYear.length > 0) {
      filterParams.batchYear = activeFilters.batchYear.join(",");
    }
    if (activeFilters.enrollmentStatus.length > 0) {
      filterParams.enrollmentStatus = activeFilters.enrollmentStatus.join(",");
    }
    if (activeFilters.enrollmentStartDate) {
      filterParams.enrollmentStartDate = activeFilters.enrollmentStartDate;
    }
    if (activeFilters.enrollmentEndDate) {
      filterParams.enrollmentEndDate = activeFilters.enrollmentEndDate;
    }

    setIsLoading(true);

    api
      .get("/students/filter", { params: filterParams })
      .then((response) => {
        const { data, meta: responsePagination } = response.data;
        setStudents(data || []);
        setLocalPagination({
          total: responsePagination?.total || 0,
          totalPages: responsePagination?.totalPages || 0,
          page: responsePagination?.page || 1,
          limit: responsePagination?.limit || 10,
        });
      })
      .catch((error) => {
        console.error("Failed to fetch filtered students:", error);
        setStudents([]);
        setLocalPagination({
          total: 0,
          totalPages: 0,
          page: 1,
          limit: pagination.pageSize,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    debouncedSearchTerm,
    activeFilters,
  ]);

  const handleEditStudent = (student: Student) => {
    setEditingStudent(student);
    setAddStudentDialogOpen(true);
  };

  const handleDeleteRows = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const selectedStudents = selectedRows.map((row) => row.original);
    const idsParam = selectedStudents.map((student) => student.id).join(",");

    api
      .delete(`/students/permanent/${idsParam}`)
      .then(() => {
        // Refresh data using the filter endpoint instead of Redux
        const filterParams = {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: debouncedSearchTerm || undefined,
        };

        setIsLoading(true);
        return api.get("/students/filter", { params: filterParams });
      })
      .then((response) => {
        if (response) {
          const { data, meta: responsePagination } = response.data;
          setStudents(data || []);
          setLocalPagination({
            total: responsePagination?.total || 0,
            totalPages: responsePagination?.totalPages || 0,
            page: responsePagination?.page || 1,
            limit: responsePagination?.limit || 10,
          });
        }
        table.resetRowSelection();
      })
      .catch((error) => {
        console.error("Failed to delete students:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Function to refresh data using filter endpoint
  const refreshData = async () => {
    const filterParams = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm || undefined,
    };

    setIsLoading(true);

    try {
      const response = await api.get("/students/filter", {
        params: filterParams,
      });
      const { data, meta: responsePagination } = response.data;
      setStudents(data || []);
      setLocalPagination({
        total: responsePagination?.total || 0,
        totalPages: responsePagination?.totalPages || 0,
        page: responsePagination?.page || 1,
        limit: responsePagination?.limit || 10,
      });
    } catch (error) {
      console.error("Failed to refresh students:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Payment-related functions for ACCOUNTANT role
  const fetchStudentFinancialDetails = async (studentId: string) => {
    console.log("coming here");
    try {
      setModalLoading(true);

      // Fetch basic financial details
      const response = (
        await api.get(`/financial/student/${studentId}/financial-details`)
      ).data;
      if (response.success) {
        setSelectedStudent(response.data);

        // Try to fetch year-wise fee structure
        try {
          const feeStructureResponse =
            await studentService.getStudentFeeStructure(studentId);
          setStudentFeeStructure(feeStructureResponse);
        } catch (feeError) {
          // If year-wise fee structure is not available (legacy student), that's OK
          console.log(
            "Year-wise fee structure not available for this student (legacy enrollment)"
          );
          setStudentFeeStructure(null);
        }

        setShowStudentModal(true);
      } else {
        console.error(response.message || "Failed to load student details");
      }
    } catch (err) {
      console.error("Error fetching student details:", err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDirectPayment = (student: Student) => {
    // Convert student data to payment form format
    const paymentData = {
      studentId: student.id,
      rollNumber: student.rollNumber,
      studentName: `${student.firstName} ${student.lastName}`,
      fatherName: student.fatherName,
      courseGroup: student.courseGroup.name,
      totalFee: student.totalFee ? parseFloat(student.totalFee) : 0,
      paidAmount: student.paidAmount || 0,
      dueAmount: student.dueAmount || 0,
    };
    setPaymentFormData(paymentData);
    setShowPaymentDialog(true);
  };

  const searchStudentByRollNumber = async () => {
    if (!searchRollNumber.trim()) {
      alert("Please enter a roll number");
      return;
    }

    try {
      setSearchLoading(true);
      const token = localStorage.getItem("token");
      const response = (
        await api.get(`students?search=${searchRollNumber.trim()}&limit=1`)
      ).data;
      // const response = await fetch(`/api/students?search=${searchRollNumber.trim()}&limit=1`, {
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   }
      // });

      // const data = await response.json();
      if (response.success && response.data.length > 0) {
        const student = response.data[0];
        setSearchedStudent(student);
        // Auto-populate payment form
        const paymentData = {
          studentId: student.id,
          rollNumber: student.rollNumber,
          studentName: `${student.firstName} ${student.lastName}`,
          fatherName: student.fatherName,
          courseGroup: student.courseGroup.name,
          totalFee: student.totalFee ? parseFloat(student.totalFee) : 0,
          paidAmount: student.paidAmount || 0,
          dueAmount: student.dueAmount || 0,
        };
        setPaymentFormData(paymentData);
        setShowStudentSearchDialog(false);
        setShowPaymentDialog(true);
      } else {
        alert("Student not found with roll number: " + searchRollNumber);
      }
    } catch (err) {
      console.error("Error searching student:", err);
      alert("Failed to search student. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handlePaymentSubmit = async (paymentData: any) => {
    try {
      setPaymentLoading(true);
      const token = localStorage.getItem("token");

      // Get student's total due amount for validation
      const studentResponse = (
        await api.get(`fees/students/${paymentData.studentId}/details`)
      ).data;
      // const studentResponse = await fetch(`http://localhost:3000/api/fees/students/${paymentData.studentId}/details`, {
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   }
      // });

      // const studentData = await studentResponse.json();

      if (!studentResponse || !studentResponse.success) {
        throw new Error("Failed to retrieve student fee data");
      }

      const paymentAmount = parseFloat(paymentData.amountPaid);
      const totalDue = studentResponse.data.feeSummary?.totalDue || 0;

      // Simple validation - check if payment exceeds total due
      if (paymentAmount > totalDue) {
        throw new Error(
          `Payment amount ₹${paymentAmount.toLocaleString()} exceeds total due amount of ₹${totalDue.toLocaleString()}. ` +
            `Maximum amount you can pay is ₹${totalDue.toLocaleString()}.`
        );
      }

      if (totalDue <= 0) {
        throw new Error(
          "No outstanding balance found for this student. All fees have been paid."
        );
      }

      // Get any installment ID for backwards compatibility (optional now)
      const installmentsResponse = (
        await api.get(
          `fees/students/${paymentData.studentId}/payable-installments`
        )
      ).data;
      // const installmentsResponse = await fetch(`http://localhost:3000/api/fees/students/${paymentData.studentId}/payable-installments`, {
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   }
      // });

      let installmentId = null;
      if (
        installmentsResponse.success &&
        installmentsResponse.data.installments?.length > 0
      ) {
        installmentId = installmentsResponse.data.installments[0].id;
      }

      // Create payment - the backend will handle all the complexity
      const paymentRequestBody: any = {
        studentId: paymentData.studentId,
        amountPaid: paymentAmount,
        paymentMode: paymentData.paymentMode.toLowerCase(),
        transactionId: paymentData.transactionId || null,
        remarks: paymentData.remarks || null,
        academicYear: paymentData.academicYear,
      };

      // Only include installmentId if we have one (it's optional now)
      if (installmentId) {
        paymentRequestBody.installmentId = installmentId;
      }
      const response = (await api.post("/fees/payments", paymentRequestBody))
        .data;

      // const response = await fetch('/api/fees/payments', {
      //   method: 'POST',
      //   headers: {
      //     'Authorization': `Bearer ${token}`,
      //     'Content-Type': 'application/json'
      //   },
      //   body: JSON.stringify(paymentRequestBody)
      // });

      if (!response.success) {
        throw new Error(response.message || "Failed to create payment");
      } else {
        const paymentResult = response.data;

        // Show success dialog with download option
        setPaymentSuccessData({
          receiptNumber: paymentResult.receiptNumber,
          amountPaid: paymentAmount,
          remainingDue: totalDue - paymentAmount,
          studentName: paymentData.studentName,
          rollNumber: paymentData.rollNumber,
        });
        setShowPaymentSuccessDialog(true);

        setShowPaymentDialog(false);
        setPaymentFormData(null);
        setSearchRollNumber("");
        setSearchedStudent(null);
        // Refresh the students list
        refreshData();
      }
    } catch (err) {
      console.error("Payment submission error:", err);
      console.error("Payment data:", paymentData);

      // More specific error messages
      let errorMessage = "Failed to record payment";
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      alert(`Failed to record payment: ${errorMessage}`);
    } finally {
      setPaymentLoading(false);
    }
  };

  const downloadPaymentReceipt = async (receiptNumber: string) => {
    try {
      setDownloadingReceipt(true);
      setDownloadingReceiptNumber(receiptNumber);

      const response = await api.get(
        `/fees/receipts/${receiptNumber}/download`,
        {
          // Use arraybuffer to avoid any axios/blob quirks
          responseType: "arraybuffer",
        }
      );

      // Create a Blob from the raw binary data
      const blob = new Blob([response.data], { type: "application/pdf" });

      // Create a temporary URL and open in a new tab for preview
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");

      // Optionally, if you also want to trigger a download instead of just preview:
      // const a = document.createElement("a");
      // a.href = url;
      // a.download = `receipt-${receiptNumber}.pdf`;
      // document.body.appendChild(a);
      // a.click();
      // document.body.removeChild(a);

      // Cleanup URL object after a delay to allow the browser to load it
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60_000);
    } catch (err) {
      console.error("Download error:", err);
      alert("Failed to open receipt. Please try again.");
    } finally {
      setDownloadingReceipt(false);
      setDownloadingReceiptNumber(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const shareOnWhatsApp = (
    phoneNumber: string,
    payment: FeePayment,
    student: StudentFinancialDetails,
    contactType: string
  ) => {
    if (!phoneNumber) {
      alert(`${contactType} phone number is not available for this student.`);
      return;
    }

    // Clean the phone number (remove spaces, hyphens, etc.)
    const cleanPhoneNumber = phoneNumber.replace(/[^\d]/g, "");

    // Format the phone number for WhatsApp (add country code if needed)
    const formattedPhone = cleanPhoneNumber.startsWith("91")
      ? cleanPhoneNumber
      : `91${cleanPhoneNumber}`;

    // Create the receipt message
    const studentName = `${student.firstName} ${student.lastName}`;
    const collegeName = "Adarsha University"; // Fallback name

    const message = `🎓 ${collegeName}
📋 *Fee Payment Receipt*

👨‍🎓 *Student:* ${studentName}
🆔 *Roll Number:* ${student.rollNumber}
👨 *Father:* ${student.fatherName}

💳 *Payment Details:*
📝 Receipt No: ${payment.receiptNumber}
💰 Amount: ${formatCurrency(payment.amount)}
📅 Date: ${formatDate(payment.paymentDate.toString())}
💸 Mode: ${payment.paymentMode}
${payment.transactionId ? `🏦 Transaction ID: ${payment.transactionId}` : ""}
📖 Description: ${payment.description || payment.semester || "Fee Payment"}

✅ Payment confirmed and recorded successfully.

📞 For any queries, please contact the accounts office.

Thank you! 🙏`;

    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(
      message
    )}`;

    // Open WhatsApp in a new tab
    window.open(whatsappUrl, "_blank");
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/[^\d]/g, "");

    // Format as XXX-XXX-XXXX or show last 4 digits for privacy
    if (cleaned.length === 10) {
      return `***-***-${cleaned.slice(-4)}`;
    } else if (cleaned.length > 10) {
      // If includes country code, show last 4 digits
      return `***-***-${cleaned.slice(-4)}`;
    }

    return phoneNumber; // Return as-is if format is unexpected
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const getColumnDisplayName = (columnId: string): string => {
    const columnNames: Record<string, string> = {
      firstName: "Student Details",
      collegeName: "College",
      email: "Contact",
      courseGroupName: "Course Group",
      enrollmentDate: "Enrollment Date",
      isActive: "Status",
      fatherName: "Father Details",
      motherName: "Mother Details",
      guardianName: "Guardian Details",
      gender: "Personal Info",
      address: "Address",
      intermediateGroup: "Intermediate",
      medium: "Medium",
      academicYear: "Academic Info",
      totalFee: "Fee Details",
      totalFeeAmount: "Total Fee",
      paidAmountFinancial: "Paid Amount",
      dueAmountFinancial: "Due Amount",
      paidAmount: "Payment Status",
      profileCompleted: "Profile Status",
    };

    return columnNames[columnId] || columnId;
  };

  const getPaymentModeIcon = (mode: string) => {
    switch (mode.toLowerCase()) {
      case "online":
        return <CreditCard className="h-4 w-4 text-blue-500" />;
      case "cash":
        return <IndianRupee className="h-4 w-4 text-green-500" />;
      case "cheque":
        return <CheckCircle className="h-4 w-4 text-purple-500" />;
      default:
        return <CreditCard className="h-4 w-4 text-gray-500" />;
    }
  };

  const columns = useMemo<ColumnDef<Student>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        size: 28,
        enableSorting: false,
        enableHiding: false,
      },
      {
        header: "Student Details",
        accessorKey: "firstName",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              <div className="font-medium text-gray-900 truncate">
                {student.firstName} {student.lastName}
              </div>
              <div className="text-sm text-gray-500 truncate">
                {student.rollNumber}
              </div>
            </div>
          );
        },
        filterFn: multiColumnFilterFn,
        enableHiding: false,
      },
      {
        header: "College",
        accessorKey: "collegeName",
        cell: ({ row }) => {
          const college = row.original.college;
          return (
            <div className="space-y-1">
              <div className="font-medium text-gray-900 truncate">
                {college.name}
              </div>
              <div className="text-sm text-gray-500 truncate">
                {college.code}
              </div>
            </div>
          );
        },
        size: 180,
        enableHiding: user?.role === "SYSTEM_ADMIN",
        meta: {
          showForRoles: ["SYSTEM_ADMIN"],
        },
      },
      {
        header: "Contact",
        accessorKey: "email",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              <div
                className="text-sm text-gray-800"
                style={{ wordBreak: "break-word" }}
              >
                {student.email}
              </div>
              {(student.studentPhone || student.phone) && (
                <div
                  className="text-sm text-gray-800"
                  style={{ wordBreak: "break-word" }}
                >
                  {student.studentPhone || student.phone}
                </div>
              )}
            </div>
          );
        },
        size: 200,
      },
      {
        header: "Course Group",
        accessorKey: "courseGroupName",
        cell: ({ row }) => {
          const courseGroup = row.original.courseGroup;
          return (
            <div className="space-y-1">
              <div className="font-medium text-gray-900 truncate">
                {courseGroup.name}
              </div>
              <div className="text-sm text-gray-500 truncate">
                {courseGroup.code}
              </div>
            </div>
          );
        },
        size: 200,
      },
      {
        header: "Enrollment Date",
        accessorKey: "enrollmentDate",
        cell: ({ row }) => (
          <div className="flex items-center text-sm text-gray-800">
            <Calendar className="h-3 w-3 mr-1 shrink-0" />
            <span className="truncate">
              {new Date(row.getValue("enrollmentDate")).toLocaleDateString()}
            </span>
          </div>
        ),
        size: 140,
      },
      {
        header: "Status",
        accessorKey: "isActive",
        cell: ({ row }) => {
          const enrollmentStatus = row.original.enrollmentStatus;
          const isActive = enrollmentStatus === "active";
          return (
            <Badge
              className={cn(
                !isActive &&
                  "bg-muted-foreground/60 hover:bg-muted-foreground/60 text-primary-foreground hover:text-primary-foreground"
              )}
            >
              {isActive ? "Active" : enrollmentStatus}
            </Badge>
          );
        },
        size: 100,
      },
      {
        header: "Father Details",
        accessorKey: "fatherName",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              {student.fatherName && (
                <div className="flex items-center text-sm text-gray-800">
                  <span>{student.fatherName}</span>
                </div>
              )}
              {student.fatherPhone && (
                <div className="flex items-center text-sm text-gray-800">
                  <span>{student.fatherPhone}</span>
                </div>
              )}
              {!student.fatherName && !student.fatherPhone && (
                <span className="text-sm text-gray-400">N/A</span>
              )}
            </div>
          );
        },
        size: 200,
      },
      {
        header: "Mother Details",
        accessorKey: "motherName",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              {student.motherName && (
                <div className="flex items-center text-sm text-gray-800">
                  <span>{student.motherName}</span>
                </div>
              )}
              {student.motherPhone && (
                <div className="flex items-center text-sm text-gray-800">
                  <span>{student.motherPhone}</span>
                </div>
              )}
              {!student.motherName && !student.motherPhone && (
                <span className="text-sm text-gray-400">N/A</span>
              )}
            </div>
          );
        },
        size: 200,
      },
      {
        header: "Guardian Details",
        accessorKey: "guardianName",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              {student.guardianName && (
                <div className="flex items-center text-sm text-gray-800">
                  <span>{student.guardianName}</span>
                </div>
              )}
              {student.guardianPhone && (
                <div className="flex items-center text-sm text-gray-600">
                  <span>{student.guardianPhone}</span>
                </div>
              )}
              {!student.guardianName && !student.guardianPhone && (
                <span className="text-sm text-gray-400">N/A</span>
              )}
            </div>
          );
        },
        size: 200,
      },
      {
        header: "Personal Info",
        accessorKey: "gender",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              {student.gender && (
                <div className="text-sm text-gray-800">{student.gender}</div>
              )}
              {student.dob && (
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-3 w-3 mr-1 shrink-0" />
                  <span className="truncate">
                    {new Date(student.dob).toLocaleDateString()}
                  </span>
                </div>
              )}
              {!student.gender && !student.dob && (
                <span className="text-sm text-gray-400">N/A</span>
              )}
            </div>
          );
        },
        size: 160,
      },
      {
        header: "Address",
        accessorKey: "address",
        cell: ({ row }) => {
          const address = row.original.address;
          return address ? (
            <div className="flex items-center text-sm text-gray-800">
              <span>{address}</span>
            </div>
          ) : (
            <span className="text-sm text-gray-400">N/A</span>
          );
        },
        size: 200,
      },
      {
        header: "Intermediate",
        accessorKey: "intermediateGroup",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              {student.intermediateGroup && (
                <div className="text-sm text-gray-800">
                  <span className="font-medium">Group:</span>{" "}
                  {student.intermediateGroup}
                </div>
              )}
              {student.intermediateCollege && (
                <div className="text-sm text-gray-800 truncate">
                  <span className="font-medium">College:</span>{" "}
                  {student.intermediateCollege}
                </div>
              )}
              {student.intermediateMarks && (
                <div className="text-sm text-gray-800">
                  <span className="font-medium">Marks:</span>{" "}
                  {student.intermediateMarks}
                </div>
              )}
              {!student.intermediateGroup &&
                !student.intermediateCollege &&
                !student.intermediateMarks && (
                  <span className="text-sm text-gray-400">N/A</span>
                )}
            </div>
          );
        },
        size: 220,
        enableHiding: !["ACCOUNTANT"].includes(user?.role!),
        meta: {
          showForRoles: ["SYSTEM_ADMIN", "COLLEGE_ADMIN", "STAFF"],
        },
      },
      {
        header: "Medium",
        accessorKey: "medium",
        cell: ({ row }) => {
          const medium = row.original.medium;
          return medium ? (
            <div className="flex items-center text-sm text-gray-800">
              <span>{medium}</span>
            </div>
          ) : (
            <span className="text-sm text-gray-400">N/A</span>
          );
        },
        size: 120,
        enableHiding: !["ACCOUNTANT"].includes(user?.role!),
        meta: {
          showForRoles: ["SYSTEM_ADMIN", "COLLEGE_ADMIN", "STAFF"],
        },
      },
      {
        header: "Academic Info",
        accessorKey: "academicYear",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              {student.academicYear && (
                <div className="text-sm text-gray-800">
                  <span className="font-medium">Year:</span>{" "}
                  {student.academicYear}
                </div>
              )}
              {student.batchYear && (
                <div className="text-sm text-gray-800">
                  <span className="font-medium">Batch:</span>{" "}
                  {student.batchYear}
                </div>
              )}
              {!student.academicYear && !student.batchYear && (
                <span className="text-sm text-gray-400">N/A</span>
              )}
            </div>
          );
        },
        size: 140,
      },
      {
        header: "Fee Details",
        accessorKey: "totalFee",
        cell: ({ row }) => {
          const student = row.original;
          return (
            <div className="space-y-1">
              {student.finalFee && (
                <div className="text-sm text-green-600">
                  <span className="font-medium">
                    Final: ₹{parseFloat(student.finalFee).toLocaleString()}
                  </span>
                </div>
              )}
              {student.totalFee && (
                <div className="flex items-center text-sm text-gray-800">
                  <span className="font-medium">
                    Fee: ₹{parseFloat(student.totalFee).toLocaleString()}
                  </span>
                </div>
              )}
              {student.discountAmount &&
                parseFloat(student.discountAmount) > 0 && (
                  <div className="text-sm text-red-400">
                    <span className="font-medium">
                      Discount: ₹
                      {parseFloat(student.discountAmount).toLocaleString()}
                    </span>
                    {student.discountReason && (
                      <div className="text-xs text-gray-500">
                        ({student.discountReason})
                      </div>
                    )}
                  </div>
                )}
              {!student.totalFee && !student.finalFee && (
                <span className="text-sm text-gray-400">N/A</span>
              )}
            </div>
          );
        },
        size: 200,
        enableHiding: !["SYSTEM_ADMIN"].includes(user?.role!),
        meta: {
          showForRoles: ["ACCOUNTANT", "COLLEGE_ADMIN", "STAFF"],
        },
      },
      {
        header: "Total Fee",
        accessorKey: "totalFeeAmount",
        cell: ({ row }) => {
          const student = row.original;
          const totalFee = student.finalFee
            ? parseFloat(student.finalFee)
            : student.totalFee
            ? parseFloat(student.totalFee)
            : 0;
          return <span>{formatCurrency(totalFee)}</span>;
        },
        size: 120,
        enableHiding: !["SYSTEM_ADMIN"].includes(user?.role!),
        meta: {
          showForRoles: ["ACCOUNTANT", "COLLEGE_ADMIN", "STAFF"],
        },
      },
      {
        header: "Paid Amount",
        accessorKey: "paidAmountFinancial",
        cell: ({ row }) => {
          const student = row.original;
          const paidAmount = student.paidAmount || 0;
          return (
            <div className="text-right">
              <span className="font-medium text-green-600">
                {formatCurrency(paidAmount)}
              </span>
            </div>
          );
        },
        size: 120,
        enableHiding: !["STAFF", "SYSTEM_ADMIN"].includes(user?.role!),
        meta: {
          showForRoles: ["ACCOUNTANT", "COLLEGE_ADMIN"],
        },
      },
      {
        header: "Due Amount",
        accessorKey: "dueAmountFinancial",
        cell: ({ row }) => {
          const student = row.original;
          const dueAmount = student.dueAmount || 0;
          const isOverpaid = dueAmount < 0;
          const isPaid = dueAmount === 0;
          return (
            <div className="text-right">
              <span
                className={`font-medium ${
                  isOverpaid
                    ? "text-green-600"
                    : isPaid
                    ? "text-gray-500"
                    : "text-red-600"
                }`}
              >
                {formatCurrency(Math.abs(dueAmount))}
                {isOverpaid && " (Excess)"}
              </span>
            </div>
          );
        },
        size: 120,
        enableHiding: !["STAFF", "SYSTEM_ADMIN"].includes(user?.role!),
        meta: {
          showForRoles: ["ACCOUNTANT", "COLLEGE_ADMIN"],
        },
      },
      {
        header: "Payment Status",
        accessorKey: "paidAmount",
        cell: ({ row }) => {
          const student = row.original;
          const paidAmount = student.paidAmount || 0;
          const dueAmount = student.dueAmount || 0;
          const isOverpaid = dueAmount < 0;
          const isPending = dueAmount > 0;

          return (
            <div className="space-y-1">
              <div className="text-sm text-gray-800">
                <span className="font-medium">
                  Paid: ₹{paidAmount.toLocaleString()}
                </span>
              </div>
              <div
                className={`text-sm ${
                  isOverpaid
                    ? "text-green-600"
                    : isPending
                    ? "text-red-400"
                    : "text-gray-600"
                }`}
              >
                <span className="font-medium">
                  {isOverpaid
                    ? "Excess: ₹" + Math.abs(dueAmount).toLocaleString()
                    : isPending
                    ? "Due: ₹" + dueAmount.toLocaleString()
                    : "Paid in Full"}
                </span>
              </div>
            </div>
          );
        },
        size: 160,
        enableHiding: !["STAFF", "SYSTEM_ADMIN"].includes(user?.role!),
        meta: {
          showForRoles: ["ACCOUNTANT", "COLLEGE_ADMIN"],
        },
      },
      {
        header: "Profile Status",
        accessorKey: "profileCompleted",
        cell: ({ row }) => {
          const profileCompleted = row.original.profileCompleted;
          return (
            <div className="flex items-center text-sm">
              {profileCompleted ? (
                <>
                  <UserCheck className="h-3 w-3 mr-1 text-green-500 shrink-0" />
                  <span className="text-green-700">Complete</span>
                </>
              ) : (
                <>
                  <User className="h-3 w-3 mr-1 text-orange-500 shrink-0" />
                  <span className="text-orange-700">Incomplete</span>
                </>
              )}
            </div>
          );
        },
        size: 120,
        enableHiding: false,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <RowActions
            row={row}
            onEdit={handleEditStudent}
            onRefresh={refreshData}
            userRole={user?.role}
            onViewFinancialDetails={fetchStudentFinancialDetails}
            onAddPayment={handleDirectPayment}
          />
        ),
        size: 60,
        enableHiding: false,
      },
    ],
    [handleEditStudent, refreshData]
  );

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== debouncedSearchTerm) {
        setDebouncedSearchTerm(searchTerm);
        setSearchInProgress(false);
      }
    }, 500); // 500ms delay

    if (searchTerm !== debouncedSearchTerm) {
      setSearchInProgress(true);
    }

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  // Use callback approach for search input to prevent re-renders
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Filter drawer handlers
  const handleApplyFilters = useCallback((filters: StudentFilters) => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setActiveFilters(filters);
  }, []);

  // Get active filter count for badge
  const getActiveFilterCount = () => {
    return (
      activeFilters.collegeId.length +
      activeFilters.courseGroupId.length +
      activeFilters.batchYear.length +
      activeFilters.enrollmentStatus.length +
      (activeFilters.enrollmentStartDate ? 1 : 0) +
      (activeFilters.enrollmentEndDate ? 1 : 0)
    );
  };

  const table = useReactTable<Student>({
    data: students || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    // Configure for server-side pagination
    manualPagination: true,
    pageCount: localPagination?.totalPages || 0,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getFacetedUniqueValues: getFacetedUniqueValues(),
    state: {
      sorting,
      pagination,
      columnFilters,
      columnVisibility,
    },
  });

  const hiddenColumnsCount = useMemo(() => {
    return table
      .getAllColumns()
      .filter((column) => column.getCanHide() && !column.getIsVisible()).length;
  }, [table.getAllColumns().map((c) => c.getIsVisible())]);

  const handleStudentAdded = () => {
    // Refresh the students list after adding a new student
    refreshData();
  };

  return (
    <>
      <div className="space-y-4 max-w-full">
        {/* Header Section - Single line with title + count on left, controls on right */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-gray-900">Students</h1>
            <Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
              {localPagination?.total || 0}
            </Badge>
          </div>

          <div className="flex items-center gap-3 w-full md:w-1/2">
            {/* Search Input */}
            <SearchInput
              id={componentId}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSearchClear={handleSearchClear}
              searchInProgress={searchInProgress}
            />

            {/* Toggle columns visibility / Delete selected - Combined in single container */}
            <div className="flex items-center border rounded-md bg-white">
              {/* Filter Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsFilterDrawerOpen(true)}
                className="relative rounded-r-none border-r"
              >
                <ListFilter className="opacity-60 h-4 w-4" />
                {getActiveFilterCount() > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums text-xs">
                    {getActiveFilterCount()}
                  </Badge>
                )}
              </Button>
              {/* Toggle columns visibility */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      table.getSelectedRowModel().rows.length > 0 &&
                        "rounded-r-none border-r",
                      "rounded-l-none"
                    )}
                  >
                    <Columns3
                      className="opacity-60 h-4 w-4"
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="center"
                  className="p-3 w-56"
                >
                  <div className="space-y-3">
                    <div className="text-xs font-medium text-muted-foreground">
                      Toggle columns
                    </div>
                    <div className="space-y-4">
                      {table
                        .getAllColumns()
                        .filter((column) => column.getCanHide())
                        .map((column) => (
                          <div
                            key={column.id}
                            className="flex items-center gap-2"
                          >
                            <Checkbox
                              id={`${componentId}-column-${column.id}`}
                              checked={column.getIsVisible()}
                              onCheckedChange={(checked: boolean) =>
                                column.toggleVisibility(!!checked)
                              }
                            />
                            <Label
                              htmlFor={`${componentId}-column-${column.id}`}
                              className="flex grow justify-between gap-2 font-normal capitalize"
                            >
                              {getColumnDisplayName(column.id)}
                            </Label>
                          </div>
                        ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Delete Icon - Only show when rows are selected */}
              {table.getSelectedRowModel().rows.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="relative rounded-l-none text-red-500 hover:text-red-500"
                    >
                      <Trash className="stroke-2 h-4 w-4" aria-hidden="true" />
                      <Badge className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums text-xs bg-red-500 hover:bg-red-500">
                        {table.getSelectedRowModel().rows.length}
                      </Badge>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Are you absolutely sure?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently
                        delete {table.getSelectedRowModel().rows.length}{" "}
                        selected{" "}
                        {table.getSelectedRowModel().rows.length === 1
                          ? "student"
                          : "students"}
                        .
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeleteRows}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>

            {/* Add Student Button - Icon only */}
            {!["ACCOUNTANT", "SYSTEM_ADMIN"].includes(user?.role!) && (
              <Button size="icon" onClick={() => setAddStudentDialogOpen(true)}>
                <Plus className="h-4 w-4" />
              </Button>
            )}

            {/* Add Payment Button - Only visible for ACCOUNTANT role */}
            {user?.role === "ACCOUNTANT" && (
              <Button
                size="icon"
                onClick={() => setShowStudentSearchDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                title="Add Payment"
              >
                <IndianRupee className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {/* Table */}
        <div className="rounded-lg border border-border bg-background w-full">
          <div className="overflow-x-auto">
            <Table className="w-full">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-transparent"
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          style={{
                            width: `${header.getSize()}px`,
                            minWidth: `${header.getSize()}px`,
                          }}
                          className="h-11"
                        >
                          {header.isPlaceholder ? null : header.column.getCanSort() ? (
                            <div
                              className={cn(
                                header.column.getCanSort() &&
                                  "flex h-full cursor-pointer select-none items-center justify-between gap-2"
                              )}
                              onClick={header.column.getToggleSortingHandler()}
                              onKeyDown={(e) => {
                                if (
                                  header.column.getCanSort() &&
                                  (e.key === "Enter" || e.key === " ")
                                ) {
                                  e.preventDefault();
                                  header.column.getToggleSortingHandler()?.(e);
                                }
                              }}
                              tabIndex={
                                header.column.getCanSort() ? 0 : undefined
                              }
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: (
                                  <ChevronUp
                                    className="shrink-0 opacity-60"
                                    size={16}
                                    strokeWidth={2}
                                    aria-hidden="true"
                                  />
                                ),
                                desc: (
                                  <ChevronDown
                                    className="shrink-0 opacity-60"
                                    size={16}
                                    strokeWidth={2}
                                    aria-hidden="true"
                                  />
                                ),
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          style={{
                            width: `${cell.column.getSize()}px`,
                            minWidth: `${cell.column.getSize()}px`,
                          }}
                          className={cn(
                            "px-4 py-2",
                            cell.column.id === "actions" ? "text-center" : "",
                            cell.column.id === "actions" ? "py-0" : ""
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      {isLoading ? (
                        <Loader variant="loading-dots" text="Loading" />
                      ) : searchTerm ? (
                        "No students found matching your search."
                      ) : (
                        "No students available."
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between gap-8">
          {/* Results per page */}
          <div className="flex items-center gap-3">
            <Label htmlFor={componentId} className="max-sm:sr-only">
              Rows per page
            </Label>
            <Select
              value={table.getState().pagination.pageSize.toString()}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger
                id={componentId}
                className="w-fit whitespace-nowrap"
              >
                <SelectValue placeholder="Select number of results" />
              </SelectTrigger>
              <SelectContent className="[&_*[role=option]>span]:end-2 [&_*[role=option]>span]:start-auto [&_*[role=option]]:pe-8 [&_*[role=option]]:ps-2">
                {[5, 10, 25, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={pageSize.toString()}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Page number information */}
          <div className="flex grow justify-end whitespace-nowrap text-sm text-muted-foreground">
            <p
              className="whitespace-nowrap text-sm text-muted-foreground"
              aria-live="polite"
            >
              <span className="text-foreground">
                {table.getState().pagination.pageIndex *
                  table.getState().pagination.pageSize +
                  1}
                -
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize,
                  localPagination?.total || 0
                )}
              </span>{" "}
              of{" "}
              <span className="text-foreground">
                {localPagination?.total || 0}
              </span>
            </p>
          </div>

          {/* Pagination buttons */}
          <div className="flex items-center gap-2">
            {/* First page button */}
            <Button
              size="icon"
              variant="outline"
              className="disabled:pointer-events-none disabled:opacity-50"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Go to first page"
            >
              <ChevronFirst className="stroke-2 h-4 w-4" aria-hidden="true" />
            </Button>
            {/* Previous page button */}
            <Button
              size="icon"
              variant="outline"
              className="disabled:pointer-events-none disabled:opacity-50"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="stroke-2 h-4 w-4" aria-hidden="true" />
            </Button>
            {/* Next page button */}
            <Button
              size="icon"
              variant="outline"
              className="disabled:pointer-events-none disabled:opacity-50"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Go to next page"
            >
              <ChevronRight className="stroke-2 h-4 w-4" aria-hidden="true" />
            </Button>
            {/* Last page button */}
            <Button
              size="icon"
              variant="outline"
              className="disabled:pointer-events-none disabled:opacity-50"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Go to last page"
            >
              <ChevronLast className="stroke-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Add Student Dialog */}
        <AddStudentDialog
          open={addStudentDialogOpen}
          onOpenChange={(open) => {
            setAddStudentDialogOpen(open);
            if (!open) {
              setEditingStudent(null); // Clear editing student when dialog closes
            }
          }}
          onStudentAdded={handleStudentAdded}
          student={editingStudent}
          mode={editingStudent ? "edit" : "add"}
        />

        {/* Payment-related dialogs for ACCOUNTANT role */}
        {["ACCOUNTANT", "COLLEGE_ADMIN"].includes(user?.role!) && (
          <>
            {/* Student Financial Details Modal */}
            <Dialog open={showStudentModal} onOpenChange={setShowStudentModal}>
              <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
                {/* Fixed Header */}
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0">
                  <DialogTitle>Student Financial Details</DialogTitle>
                  <DialogDescription>
                    View comprehensive financial information and payment history
                  </DialogDescription>
                </DialogHeader>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {modalLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
                      <span className="ml-2 text-gray-600">
                        Loading financial details...
                      </span>
                    </div>
                  ) : (
                    selectedStudent && (
                      <div className="space-y-6">
                        {/* Student Info */}
                        <div className="grid grid-cols-1 gap-6">
                          <div className="space-y-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                              <h3 className="font-semibold text-gray-900 mb-3">
                                Student Information
                              </h3>
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Name:
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {selectedStudent.firstName}{" "}
                                    {selectedStudent.lastName}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Roll Number:
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {selectedStudent.rollNumber}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Father Name:
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {selectedStudent.fatherName}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Course:
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {selectedStudent.courseGroup.name}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Financial Summary */}
                          <div className="space-y-4">
                            <div className="p-4 bg-blue-50 rounded-lg">
                              <h3 className="font-semibold text-gray-900 mb-3">
                                Financial Summary
                              </h3>
                              <div className="space-y-3">
                                <div className="flex justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Total Fee:
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {formatCurrency(selectedStudent.totalFee)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Total Paid:
                                  </span>
                                  <span className="text-sm font-semibold text-green-600">
                                    {formatCurrency(selectedStudent.paidAmount)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm font-medium text-gray-600">
                                    Total Due:
                                  </span>
                                  <span
                                    className={`text-sm font-semibold ${
                                      selectedStudent.dueAmount > 0
                                        ? "text-red-600"
                                        : "text-green-600"
                                    }`}
                                  >
                                    {formatCurrency(selectedStudent.dueAmount)}
                                  </span>
                                </div>

                                {/* Current Year Fee Information - from backend */}
                                {selectedStudent.currentYearFee !==
                                  undefined && (
                                  <>
                                    <hr className="border-t border-gray-300 my-3" />
                                    <div className="bg-yellow-50 p-3 rounded-lg space-y-2 border border-yellow-200">
                                      <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                        <GraduationCap className="h-4 w-4 text-yellow-600" />
                                        Current Year (
                                        {selectedStudent.currentAcademicYear})
                                      </h4>
                                      <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div className="space-y-1">
                                          <div className="text-gray-600">
                                            Year Fee
                                          </div>
                                          <div className="font-semibold text-gray-900">
                                            {formatCurrency(
                                              selectedStudent.currentYearFee
                                            )}
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          <div className="text-gray-600">
                                            Year Paid
                                          </div>
                                          <div className="font-semibold text-green-600">
                                            {formatCurrency(
                                              selectedStudent.currentYearPaid ||
                                                0
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="border-t border-yellow-200 pt-2">
                                        <div className="flex justify-between items-center">
                                          <span className="text-sm font-semibold text-gray-700">
                                            Year Due:
                                          </span>
                                          <span
                                            className={`text-lg font-bold ${
                                              (selectedStudent.currentYearDue ||
                                                0) > 0
                                                ? "text-red-600"
                                                : "text-green-600"
                                            }`}
                                          >
                                            {formatCurrency(
                                              selectedStudent.currentYearDue ||
                                                0
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Year-wise Fee Information (if available) */}
                            {studentFeeStructure && (
                              <div className="p-4 bg-green-50 rounded-lg border">
                                <div className="flex items-center gap-2 mb-3">
                                  <IndianRupee className="h-4 w-4 text-green-600" />
                                  <h4 className="font-semibold text-gray-900">
                                    Year-wise Fee Structure
                                  </h4>
                                </div>
                                <div className="space-y-3">
                                  {studentFeeStructure.feeStructure.map(
                                    (yearFee) => (
                                      <div
                                        key={yearFee.yearNumber}
                                        className="bg-white p-3 rounded border"
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-sm font-medium text-gray-800">
                                            Year {yearFee.yearNumber} (
                                            {yearFee.academicYear})
                                          </span>
                                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                            {yearFee.academicYear}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 text-xs">
                                          <div>
                                            <span className="text-gray-600">
                                              Fee:
                                            </span>
                                            <div className="font-medium">
                                              {yearFee.formattedFeeAmount}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">
                                              Discount:
                                            </span>
                                            <div className="font-medium text-red-600">
                                              {yearFee.formattedDiscountAmount}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="text-gray-600">
                                              Final:
                                            </span>
                                            <div className="font-medium text-green-600">
                                              {yearFee.formattedFinalAmount}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  )}

                                  {/* Totals Summary */}
                                  <div className="bg-gray-100 p-3 rounded border-t-2 border-blue-500">
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2">
                                      Total Summary
                                    </h5>
                                    <div className="grid grid-cols-3 gap-2 text-sm">
                                      <div>
                                        <span className="text-gray-600">
                                          Total Fee:
                                        </span>
                                        <div className="font-bold text-gray-900">
                                          {formatCurrency(
                                            studentFeeStructure.totals
                                              .totalFeeAmount
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">
                                          Total Discount:
                                        </span>
                                        <div className="font-bold text-red-600">
                                          {formatCurrency(
                                            studentFeeStructure.totals
                                              .totalDiscountAmount
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">
                                          Final Amount:
                                        </span>
                                        <div className="font-bold text-green-600">
                                          {formatCurrency(
                                            studentFeeStructure.totals
                                              .totalFinalAmount
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Payment History */}
                        <div className="border rounded-lg">
                          <div className="p-4 border-b bg-gray-50">
                            <h3 className="font-semibold text-gray-900">
                              Payment History
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              All fee payments made by this student (
                              {selectedStudent.payments.length} transactions)
                            </p>
                          </div>
                          <div className="p-4">
                            {selectedStudent.payments.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b bg-gray-50">
                                      <th className="text-left p-3">
                                        Receipt No.
                                      </th>
                                      <th className="text-left p-3">Date</th>
                                      <th className="text-left p-3">Mode</th>
                                      <th className="text-left p-3">
                                        Transaction ID
                                      </th>
                                      <th className="text-right p-3">Amount</th>
                                      <th className="text-left p-3">
                                        Description
                                      </th>
                                      <th className="text-center p-3">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedStudent.payments.map((payment) => (
                                      <tr
                                        key={payment.id}
                                        className="border-b hover:bg-gray-50"
                                      >
                                        <td className="p-3">
                                          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                            {payment.receiptNumber}
                                          </span>
                                        </td>
                                        <td className="p-3">
                                          {formatDate(
                                            payment.paymentDate.toString()
                                          )}
                                        </td>
                                        <td className="p-3">
                                          <div className="flex items-center space-x-2">
                                            {getPaymentModeIcon(
                                              payment.paymentMode
                                            )}
                                            <span>{payment.paymentMode}</span>
                                          </div>
                                        </td>
                                        <td className="p-3">
                                          <span className="font-mono text-xs">
                                            {payment.transactionId || "N/A"}
                                          </span>
                                        </td>
                                        <td className="p-3 text-right">
                                          <span className="font-medium text-green-600">
                                            {formatCurrency(payment.amount)}
                                          </span>
                                        </td>
                                        <td className="p-3">
                                          <span className="text-sm text-gray-600">
                                            {payment.description ||
                                              payment.semester ||
                                              "Fee Payment"}
                                          </span>
                                        </td>
                                        <td className="p-3 text-center">
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-8 px-3"
                                              >
                                                <MoreHorizontal className="h-4 w-4" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                              <DropdownMenuItem
                                                onClick={() =>
                                                  downloadPaymentReceipt(
                                                    payment.receiptNumber
                                                  )
                                                }
                                                disabled={
                                                  downloadingReceiptNumber ===
                                                  payment.receiptNumber
                                                }
                                              >
                                                {downloadingReceiptNumber ===
                                                payment.receiptNumber ? (
                                                  <>
                                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                    Generating PDF...
                                                  </>
                                                ) : (
                                                  <>
                                                    <Download className="h-4 w-4" />
                                                    Download Receipt
                                                  </>
                                                )}
                                              </DropdownMenuItem>

                                              {/* WhatsApp Share with Contact Selection */}
                                              <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                  <DropdownMenuItem
                                                    onSelect={(e) =>
                                                      e.preventDefault()
                                                    }
                                                  >
                                                    <IconWhatsapp />
                                                    Share via WhatsApp
                                                    <ChevronRight className="h-4 w-4 ml-auto" />
                                                  </DropdownMenuItem>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                  side="right"
                                                  sideOffset={5}
                                                >
                                                  {selectedStudent.studentPhone && (
                                                    <DropdownMenuItem
                                                      onClick={() =>
                                                        shareOnWhatsApp(
                                                          selectedStudent.studentPhone!,
                                                          payment,
                                                          selectedStudent,
                                                          "Student"
                                                        )
                                                      }
                                                    >
                                                      Student (
                                                      {formatPhoneNumber(
                                                        selectedStudent.studentPhone
                                                      )}
                                                      )
                                                    </DropdownMenuItem>
                                                  )}
                                                  {selectedStudent.fatherPhone && (
                                                    <DropdownMenuItem
                                                      onClick={() =>
                                                        shareOnWhatsApp(
                                                          selectedStudent.fatherPhone!,
                                                          payment,
                                                          selectedStudent,
                                                          "Father"
                                                        )
                                                      }
                                                    >
                                                      Father (
                                                      {formatPhoneNumber(
                                                        selectedStudent.fatherPhone
                                                      )}
                                                      )
                                                    </DropdownMenuItem>
                                                  )}
                                                  {selectedStudent.motherPhone && (
                                                    <DropdownMenuItem
                                                      onClick={() =>
                                                        shareOnWhatsApp(
                                                          selectedStudent.motherPhone!,
                                                          payment,
                                                          selectedStudent,
                                                          "Mother"
                                                        )
                                                      }
                                                    >
                                                      Mother (
                                                      {formatPhoneNumber(
                                                        selectedStudent.motherPhone
                                                      )}
                                                      )
                                                    </DropdownMenuItem>
                                                  )}
                                                  {selectedStudent.guardianPhone && (
                                                    <DropdownMenuItem
                                                      onClick={() =>
                                                        shareOnWhatsApp(
                                                          selectedStudent.guardianPhone!,
                                                          payment,
                                                          selectedStudent,
                                                          "Guardian"
                                                        )
                                                      }
                                                    >
                                                      Guardian (
                                                      {formatPhoneNumber(
                                                        selectedStudent.guardianPhone
                                                      )}
                                                      )
                                                    </DropdownMenuItem>
                                                  )}
                                                  {!selectedStudent.studentPhone &&
                                                    !selectedStudent.fatherPhone &&
                                                    !selectedStudent.motherPhone &&
                                                    !selectedStudent.guardianPhone && (
                                                      <DropdownMenuItem
                                                        disabled
                                                      >
                                                        No contact numbers
                                                        available
                                                      </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                              </DropdownMenu>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-center py-8">
                                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600">
                                  No payments recorded yet
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Student Search Dialog for Payment */}
            <Dialog
              open={showStudentSearchDialog}
              onOpenChange={setShowStudentSearchDialog}
            >
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Payment</DialogTitle>
                  <DialogDescription>
                    Enter student roll number to find and add payment
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="rollNumber"
                      className="text-sm font-medium text-gray-700"
                    >
                      Student Roll Number
                    </label>
                    <Input
                      id="rollNumber"
                      placeholder="Enter roll number (e.g., ADSH250001)"
                      value={searchRollNumber}
                      onChange={(e) => setSearchRollNumber(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          searchStudentByRollNumber();
                        }
                      }}
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowStudentSearchDialog(false);
                        setSearchRollNumber("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={searchStudentByRollNumber}
                      disabled={searchLoading || !searchRollNumber.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {searchLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Find Student
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Payment Form Dialog */}
            <PaymentFormDialog
              open={showPaymentDialog}
              onOpenChange={setShowPaymentDialog}
              paymentData={paymentFormData}
              onSubmit={handlePaymentSubmit}
              loading={paymentLoading}
            />

            {/* Payment Success Dialog */}
            <Dialog
              open={showPaymentSuccessDialog}
              onOpenChange={setShowPaymentSuccessDialog}
            >
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center space-x-2 text-green-600">
                    <CheckCircle className="h-6 w-6" />
                    <span>Payment Recorded Successfully!</span>
                  </DialogTitle>
                  <DialogDescription>
                    Payment has been successfully recorded and receipt
                    generated.
                  </DialogDescription>
                </DialogHeader>

                {paymentSuccessData && (
                  <div className="space-y-4">
                    {/* Success Summary */}
                    <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Student:
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {paymentSuccessData.studentName}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Roll Number:
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {paymentSuccessData.rollNumber}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Receipt Number:
                          </span>
                          <span className="text-sm font-semibold text-green-700 font-mono">
                            {paymentSuccessData.receiptNumber}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Amount Paid:
                          </span>
                          <span className="text-sm font-bold text-green-600">
                            {formatCurrency(paymentSuccessData.amountPaid)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600">
                            Remaining Due:
                          </span>
                          <span
                            className={`text-sm font-medium ${
                              paymentSuccessData.remainingDue > 0
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                          >
                            {formatCurrency(paymentSuccessData.remainingDue)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between space-x-3">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowPaymentSuccessDialog(false);
                          setPaymentSuccessData(null);
                        }}
                      >
                        Close
                      </Button>
                      <Button
                        onClick={() =>
                          downloadPaymentReceipt(
                            paymentSuccessData.receiptNumber
                          )
                        }
                        disabled={
                          downloadingReceiptNumber ===
                          paymentSuccessData.receiptNumber
                        }
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {downloadingReceiptNumber ===
                        paymentSuccessData.receiptNumber ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating PDF...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download Receipt
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
      <StudentFilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApplyFilters={handleApplyFilters}
        currentFilters={activeFilters}
      />
    </>
  );
};

export default Students;
